import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import { legacyRoutes } from '@/lib/legacy';
import { getAccountTickets } from '../api';
import type { TicketDetail } from '../types';

interface SupportTicketsSectionProps {
  accountId: string;
}

const PAGE_SIZE = 10;

export default function SupportTicketsSection({
  accountId,
}: SupportTicketsSectionProps) {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-tickets', accountId, page, statusFilter],
    queryFn: () =>
      getAccountTickets(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
  });

  if (isLoading) {
    return <SupportTicketsSkeleton />;
  }

  if (error) {
    return (
      <div className="py-10 text-center space-y-4">
        <p className="text-destructive">Error loading support tickets</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
          <a href={legacyRoutes.ticketsByOrg(accountId)}>
            <Button variant="outline">View in Legacy System</Button>
          </a>
        </div>
      </div>
    );
  }

  const tickets = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const hasMore = page < totalPages - 1;

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center justify-end mb-4">
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="waiting">Waiting</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {tickets.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          No support tickets found
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((ticket) => (
                <SupportTicketRow key={ticket._id} ticket={ticket} />
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {page + 1}{total > 0 ? ` of ${totalPages}` : ''} ({total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SupportTicketRow({ ticket }: { ticket: TicketDetail }) {
  const statusVariantMap: Record<
    string,
    'secondary' | 'warning' | 'success' | 'outline' | 'destructive'
  > = {
    pending: 'warning',
    waiting: 'secondary',
    complete: 'success',
    cancelled: 'outline',
  };
  const statusVariant = statusVariantMap[ticket.status] || 'secondary';

  // Priority based on important × urgent (max 25)
  const priority = ticket.priority_product || ticket.important * ticket.urgent || 0;
  const priorityLevel =
    priority >= 20 ? 'critical' : priority >= 12 ? 'high' : priority >= 6 ? 'medium' : 'low';

  const priorityVariantMap: Record<string, 'destructive' | 'warning' | 'secondary' | 'outline'> = {
    critical: 'destructive',
    high: 'warning',
    medium: 'secondary',
    low: 'outline',
  };

  // assigned_to can be:
  // - undefined/null: unassigned
  // - string: ObjectId (not populated)
  // - object: populated user with firstName/lastName/email
  // user_name may contain denormalized name from aggregation
  const getAssignedName = (): string => {
    // Try user_name first (denormalized field from aggregation)
    if (ticket.user_name) {
      return ticket.user_name;
    }
    // No assigned_to
    if (!ticket.assigned_to) {
      return 'Unassigned';
    }
    // assigned_to is just an ObjectId string
    if (typeof ticket.assigned_to === 'string') {
      return 'Assigned';
    }
    // assigned_to is a populated object
    const user = ticket.assigned_to;
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return fullName || user.email || 'Assigned';
  };
  const assignedName = getAssignedName();

  return (
    <TableRow>
      <TableCell className="font-medium">#{ticket.ticket_number}</TableCell>
      <TableCell className="max-w-[200px] truncate" title={ticket.name}>
        {ticket.name || '(No name)'}
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant}>{ticket.status}</Badge>
      </TableCell>
      <TableCell>
        <Badge variant={priorityVariantMap[priorityLevel]}>
          {priorityLevel.charAt(0).toUpperCase() + priorityLevel.slice(1)}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{assignedName}</TableCell>
      <TableCell>{formatDate(ticket.created_at)}</TableCell>
      <TableCell>
        <a
          href={legacyRoutes.ticket(ticket._id)}
          className="text-atek-blue hover:underline text-sm"
        >
          View
        </a>
      </TableCell>
    </TableRow>
  );
}

function SupportTicketsSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export { SupportTicketsSection, SupportTicketRow, SupportTicketsSkeleton };
